import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";

import { useToast } from "../context/toastContext";
import SuccessModal from "../components/modal/SuccessModal";
import { authenticatedAxios } from "../apis";
import useEventStore from "../stores/events/event";

const InputGroup = ({ label, children, hint, required }) => (
  <div className="mb-5">
    <label className="text-left block text-sm font-semibold text-gray-800 mb-2">
      {label}
      {required && <span className="text-red-500 ml-1">*</span>}
    </label>
    {children}
    {hint && <p className="text-left text-xs text-gray-500 mt-1.5">{hint}</p>}
  </div>
);

const Card = ({ title, children }) => (
  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
    {title && (
      <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
        <h2 className="text-left text-lg font-bold text-gray-900">{title}</h2>
      </div>
    )}
    <div className="p-6">{children}</div>
  </div>
);

export default function EventCreateManagement() {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const textareaRef = useRef(null);

  const createEvent = useEventStore((s) => s.actions.createEvent);
  const loadingAction = useEventStore((s) => s.loadingAction);

  // content + temp images (same pattern as blog)
  const [markdown, setMarkdown] = useState("");
  const [tempImages, setTempImages] = useState({});
  const imageCounterRef = useRef(1);

  // main fields
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [excerpt, setExcerpt] = useState("");

  // publish controls
  const [isPublishing, setIsPublishing] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // popup fields
  const [popupEnabled, setPopupEnabled] = useState(false);
  const [popupTitle, setPopupTitle] = useState("");
  const [popupText, setPopupText] = useState("");
  const [popupImage, setPopupImage] = useState(null);
  const [popupCtaText, setPopupCtaText] = useState("");
  const [popupCtaUrl, setPopupCtaUrl] = useState("");
  const [popupStartAt, setPopupStartAt] = useState("");
  const [popupEndAt, setPopupEndAt] = useState("");

  // schedule + place
  const [startAt, setStartAt] = useState("");
  const [endAt, setEndAt] = useState("");
  const [timezoneStr, setTimezoneStr] = useState("Asia/Jakarta");

  const [eventType, setEventType] = useState("ONLINE"); // ONLINE / OFFLINE
  const [platform, setPlatform] = useState("");
  const [meetingUrl, setMeetingUrl] = useState("");

  const [locationName, setLocationName] = useState("");
  const [locationAddress, setLocationAddress] = useState("");

  //speakers
  const [speakers, setSpeakers] = useState([
    { name: "", role: "", tagline: "", photo: "" },
  ]);

  const [speakerUploading, setSpeakerUploading] = useState({});
  const [speakerPickedName, setSpeakerPickedName] = useState({});

  //what will you get
  const [whatYouGet, setWhatYouGet] = useState([""]);

  const addWhatYouGet = () => setWhatYouGet((prev) => [...prev, ""]);
  const removeWhatYouGet = (idx) => setWhatYouGet((prev) => prev.filter((_, i) => i !== idx));
  const updateWhatYouGet = (idx, value) =>
    setWhatYouGet((prev) => prev.map((v, i) => (i === idx ? value : v)));

  const normalizeWhatYouGet = (list) =>
    (list || []).map((x) => (x || "").trim()).filter(Boolean);

  // ticket
  const [isFree, setIsFree] = useState(true);
  const [currency, setCurrency] = useState("USD");
  const [ticketPrice, setTicketPrice] = useState("");
  const [ticketNote, setTicketNote] = useState("");

  // register CTA
  const [registerText, setRegisterText] = useState("Register");
  const [registerUrl, setRegisterUrl] = useState("");

  // images
  const [cardImage, setCardImage] = useState(null);
  const [heroImage, setHeroImage] = useState(null);

  useEffect(() => {
    if (isFree) setTicketPrice("");
  }, [isFree]);


  const addSpeaker = () =>
    setSpeakers((prev) => [...prev, { name: "", role: "", tagline: "", photo: "" }]);

  const removeSpeaker = (idx) =>
    setSpeakers((prev) => prev.filter((_, i) => i !== idx));

  const updateSpeaker = (idx, key, value) =>
    setSpeakers((prev) =>
      prev.map((s, i) => (i === idx ? { ...s, [key]: value } : s))
    );

  // Optional: avoid saving totally empty speakers
  const normalizeSpeakers = (list) =>
    (list || [])
      .map((s) => ({
        name: (s.name || "").trim(),
        role: (s.role || "").trim(),
        tagline: (s.tagline || "").trim(),
        photo: (s.photo || "").trim(),
      }))
      .filter((s) => s.name); // require name

  const uploadSpeakerPhoto = async (idx, file) => {
    const MAX_SIZE = 3 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      addToast("error", "Speaker photo too large. Max 3MB.");
      return;
    }

    const fdImg = new FormData();
    fdImg.append("event_image", file);
    fdImg.append("slug", slug || generateSlug(title) || "temp");

    const token = localStorage.getItem("adminAccessToken");
    console.log("token", token);

    const { data } = await authenticatedAxios.post(
      "/api/admin/event/content/upload-image",
      fdImg,
      { headers: { "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${token}`,
       } }
    );

    if (!data?.url) {
      addToast("error", "Failed uploading speaker photo.");
      return;
    }

    updateSpeaker(idx, "photo", data.url);
  };

  const generateSlug = (t) =>
    t
      .trim()
      .toLowerCase()
      .replace(/[^\w\u3131-\uD79D]+/g, "-")
      .replace(/^-+|-+$/g, "");

  const handleTitleChange = (e) => {
    const val = e.target.value;
    setTitle(val);
    setSlug(generateSlug(val));
  };

  const insertAtCursor = (text) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;

    const before = markdown.substring(0, start);
    const after = markdown.substring(end);

    const newValue = before + text + after;
    setMarkdown(newValue);

    setTimeout(() => {
      textarea.selectionStart = textarea.selectionEnd = start + text.length;
    }, 0);
  };

  const handleMarkdownTempImage = async (file) => {
    const MAX_SIZE = 3 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      addToast("error", "Image is too large. Max 3MB allowed.");
      return;
    }

    const key = `image-${imageCounterRef.current++}`;
    const blobUrl = URL.createObjectURL(file);

    setTempImages((prev) => ({ ...prev, [key]: blobUrl }));
    insertAtCursor(`\n\n{{${key}}}\n\n`);
  };

  const handlePublish = async () => {
    if (!title || !slug || !markdown) {
      addToast("error", "Please fill required fields: title, slug, content.");
      return;
    }

    setIsPublishing(true);

    try {
      // Step 1: upload inline images and replace placeholders
      let finalMarkdown = markdown;

      for (const [key, blobUrl] of Object.entries(tempImages)) {
        if (!finalMarkdown.includes(`{{${key}}}`)) continue;

        const response = await fetch(blobUrl);
        const blob = await response.blob();
        const file = new File([blob], `${key}.jpg`, { type: blob.type });

        const fdImg = new FormData();
        fdImg.append("event_image", file);
        fdImg.append("slug", slug);
        
        const token = localStorage.getItem("adminAccessToken");
        const { data } = await authenticatedAxios.post(
          "/api/admin/event/content/upload-image",
          fdImg,
          { headers: { "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
           } }
        );

        if (data?.url) {
          finalMarkdown = finalMarkdown.replace(
            new RegExp(`\\{\\{${key}\\}\\}`, "g"),
            `![image](${data.url})`
          );
        } else {
          addToast("error", `Failed uploading inline image ${key}`);
          setIsPublishing(false);
          return;
        }
      }

      // Step 2: publish event
      const mdBlob = new Blob([finalMarkdown], { type: "text/markdown" });
      const mdFile = new File([mdBlob], `${slug}.md`, { type: "text/markdown" });

      const fd = new FormData();
      fd.append("title", title);
      fd.append("slug", slug);
      fd.append("excerpt", excerpt);
      fd.append("markdown", mdFile);
      fd.append("is_published", "true");

      // popup
      fd.append("popup_enabled", popupEnabled ? "true" : "false");
      fd.append("popup_title", popupTitle);
      fd.append("popup_text", popupText);
      fd.append("popup_cta_text", popupCtaText);
      fd.append("popup_cta_url", popupCtaUrl);
      if (popupStartAt) fd.append("popup_start_at", popupStartAt);
      if (popupEndAt) fd.append("popup_end_at", popupEndAt);
      if (popupImage) fd.append("popup_image", popupImage);

      // schedule
      if (startAt) fd.append("start_at", startAt);
      if (endAt) fd.append("end_at", endAt);
      fd.append("timezone_str", timezoneStr);

      // venue
      fd.append("event_type", eventType);
      fd.append("platform", platform);
      fd.append("meeting_url", meetingUrl);
      fd.append("location_name", locationName);
      fd.append("location_address", locationAddress);

      // ticket
      fd.append("is_free", isFree ? "true" : "false");
      fd.append("currency", currency);
      fd.append("ticket_price", ticketPrice);
      fd.append("ticket_note", ticketNote);

      //speakers
      fd.append("speakers_json", JSON.stringify(normalizeSpeakers(speakers)));

      //what will you get
      fd.append("what_you_get_json", JSON.stringify(normalizeWhatYouGet(whatYouGet)));

      // register
      fd.append("register_text", registerText);
      fd.append("register_url", registerUrl);

      // images
      if (cardImage) fd.append("card_image", cardImage);
      if (heroImage) fd.append("hero_image", heroImage);

      const ok = await createEvent(fd);

      if (ok) {
        Object.values(tempImages).forEach((url) => URL.revokeObjectURL(url));
        setTempImages({});
        setShowSuccess(true);

        setTimeout(() => {
          setShowSuccess(false);
          navigate("/admin/event-manage");
        }, 1200);
      } else {
        addToast("error", "Failed to publish event.");
      }
    } catch (err) {
      console.error(err);
      addToast("error", "Unexpected error occurred.");
    } finally {
      setIsPublishing(false);
    }
  };

  useEffect(() => {
    return () => {
      Object.values(tempImages).forEach((url) => URL.revokeObjectURL(url));
    };
  }, [tempImages]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-50">
      <SuccessModal
        show={showSuccess}
        message="Event published successfully!"
        onClose={() => setShowSuccess(false)}
      />

      <div className="max-w-screen-2xl mx-auto px-6 py-8">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-8">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Create Event</h1>
              <p className="text-sm text-gray-600 mt-1">
                Publish an event page + optional popup banner.
              </p>
            </div>

            <div className="flex gap-3">
            <button
              onClick={() => navigate(-1)}
              className="px-5 py-2.5 rounded-xl bg-[#dc2626] text-white hover:bg-[#b91c1c]"
            >
              Cancel
            </button>
              <button
                onClick={handlePublish}
                disabled={isPublishing || loadingAction}
                className={`px-6 py-2.5 rounded-xl text-white font-semibold ${
                  isPublishing || loadingAction
                    ? "bg-blue-400 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700"
                }`}
              >
                {isPublishing ? "Publishing..." : "Publish Event"}
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
          {/* Left: fields */}
          <div className="xl:col-span-5 space-y-7">
            <Card title="Event Information">
              <InputGroup label="Title" required hint="Slug is auto-generated from title">
                <input
                  className="w-full border-2 border-gray-200 rounded-xl px-4 py-3"
                  value={title}
                  onChange={handleTitleChange}
                  placeholder="New Year Event 2026"
                />
              </InputGroup>

              <InputGroup label="Slug" hint="You can edit this">
                <input
                  className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 font-mono text-sm bg-gray-50"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  placeholder="new-year-event-2026"
                />
              </InputGroup>

              <InputGroup label="Excerpt" hint="Short summary (optional)">
                <textarea
                  rows={4}
                  className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 resize-none"
                  value={excerpt} 
                  onChange={(e) => setExcerpt(e.target.value)}
                />
              </InputGroup>
            </Card>

            <Card title="Schedule">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InputGroup label="Start At" hint="Local datetime">
                  <input
                    type="datetime-local"
                    className="w-full border-2 border-gray-200 rounded-xl px-4 py-3"
                    value={startAt}
                    onChange={(e) => setStartAt(e.target.value)}
                  />
                </InputGroup>

                <InputGroup label="End At" hint="Optional">
                  <input
                    type="datetime-local"
                    className="w-full border-2 border-gray-200 rounded-xl px-4 py-3"
                    value={endAt}
                    onChange={(e) => setEndAt(e.target.value)}
                  />
                </InputGroup>
              </div>

              <InputGroup label="Timezone" hint='Default: "Asia/Jakarta"'>
                <input
                  className="w-full border-2 border-gray-200 rounded-xl px-4 py-3"
                  value={timezoneStr}
                  onChange={(e) => setTimezoneStr(e.target.value)}
                  placeholder="Asia/Jakarta"
                />
              </InputGroup>
            </Card>

            <Card title="Venue">
              <InputGroup label="Event Type">
                <select
                  className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 bg-white"
                  value={eventType}
                  onChange={(e) => setEventType(e.target.value)}
                >
                  <option value="ONLINE">ONLINE</option>
                  <option value="OFFLINE">OFFLINE</option>
                </select>
              </InputGroup>

              {eventType === "ONLINE" ? (
                <>
                  <InputGroup label="Platform" hint="Zoom, Google Meet, YouTube Live, etc.">
                    <input
                      className="w-full border-2 border-gray-200 rounded-xl px-4 py-3"
                      value={platform}
                      onChange={(e) => setPlatform(e.target.value)}
                      placeholder="Zoom"
                    />
                  </InputGroup>

                  <InputGroup label="Meeting URL" hint="Public join link">
                    <input
                      className="w-full border-2 border-gray-200 rounded-xl px-4 py-3"
                      value={meetingUrl}
                      onChange={(e) => setMeetingUrl(e.target.value)}
                      placeholder="https://..."
                    />
                  </InputGroup>
                </>
              ) : (
                <>
                  <InputGroup label="Location Name" hint="Venue name">
                    <input
                      className="w-full border-2 border-gray-200 rounded-xl px-4 py-3"
                      value={locationName}
                      onChange={(e) => setLocationName(e.target.value)}
                      placeholder="Location"
                    />
                  </InputGroup>

                  <InputGroup label="Address" hint="Full address (optional)">
                    <textarea
                      rows={3}
                      className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 resize-none"
                      value={locationAddress}
                      onChange={(e) => setLocationAddress(e.target.value)}
                      placeholder="Jl. ..."
                    />
                  </InputGroup>
                </>
              )}
            </Card>

            <Card title="Ticket & Register">
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm font-semibold text-gray-800">Free Event</span>
                <input
                  type="checkbox"
                  checked={isFree}
                  onChange={(e) => setIsFree(e.target.checked)}
                  className="w-5 h-5"
                />
              </div>

              {!isFree && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputGroup label="Currency" hint="USD / IDR / SGD etc.">
                    <input
                      className="w-full border-2 border-gray-200 rounded-xl px-4 py-3"
                      value={currency}
                      onChange={(e) => setCurrency(e.target.value)}
                      placeholder="USD"
                    />
                  </InputGroup>

                  <InputGroup label="Ticket Price" hint="Numbers only (e.g. 50 or 50000)">
                    <input
                      className="w-full border-2 border-gray-200 rounded-xl px-4 py-3"
                      value={ticketPrice}
                      onChange={(e) => setTicketPrice(e.target.value)}
                      placeholder="50"
                    />
                  </InputGroup>
                </div>
              )}

              <InputGroup label="Ticket Note" hint="Short copy near the price (optional)">
                <input
                  className="w-full border-2 border-gray-200 rounded-xl px-4 py-3"
                  value={ticketNote}
                  onChange={(e) => setTicketNote(e.target.value)}
                  placeholder="Only $50 untuk investasi ilmu..."
                />
              </InputGroup>

              <InputGroup label="Register Button Text">
                <input
                  className="w-full border-2 border-gray-200 rounded-xl px-4 py-3"
                  value={registerText}
                  onChange={(e) => setRegisterText(e.target.value)}
                  placeholder="Register"
                />
              </InputGroup>

              <InputGroup label="Register URL" hint="Where the button should link to">
                <input
                  className="w-full border-2 border-gray-200 rounded-xl px-4 py-3"
                  value={registerUrl}
                  onChange={(e) => setRegisterUrl(e.target.value)}
                  placeholder="https://..."
                />
              </InputGroup>
            </Card>

            <Card title="Images">
              <InputGroup label="Thumbnail Image" hint="Used for the event list card">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setCardImage(e.target.files?.[0] || null)}
                />
                {cardImage && (
                  <p className="text-xs text-gray-500 mt-2">
                    Selected: <span className="font-mono">{cardImage.name}</span>
                  </p>
                )}
              </InputGroup>

              <InputGroup label="Hero Image" hint="Optional top image on detail page">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setHeroImage(e.target.files?.[0] || null)}
                />
                {heroImage && (
                  <p className="text-xs text-gray-500 mt-2">
                    Selected: <span className="font-mono">{heroImage.name}</span>
                  </p>
                )}
              </InputGroup>
            </Card>

            <Card title="Speakers">
              <div className="space-y-4">
                {speakers.map((sp, idx) => (
                  <div key={idx} className="border border-gray-200 rounded-xl p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="font-semibold text-gray-900">Speaker #{idx + 1}</div>
                      {speakers.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeSpeaker(idx)}
                          className="text-sm px-3 py-1 rounded-lg border border-red-200 text-red-700 hover:bg-red-50"
                        >
                          Remove
                        </button>
                      )}
                    </div>

                    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <InputGroup label="Name" required>
                        <input
                          className="w-full border-2 border-gray-200 rounded-xl px-4 py-3"
                          value={sp.name}
                          onChange={(e) => updateSpeaker(idx, "name", e.target.value)}
                          placeholder="Speaker"
                        />
                      </InputGroup>

                      <InputGroup label="Role / Title">
                        <input
                          className="w-full border-2 border-gray-200 rounded-xl px-4 py-3"
                          value={sp.role}
                          onChange={(e) => updateSpeaker(idx, "role", e.target.value)}
                          placeholder="Marketing Manager"
                        />
                      </InputGroup>

                      <InputGroup label="Short Description" hint="One line, like your mockup">
                        <input
                          className="w-full border-2 border-gray-200 rounded-xl px-4 py-3"
                          value={sp.tagline}
                          onChange={(e) => updateSpeaker(idx, "tagline", e.target.value)}
                          placeholder="Short Desc"
                        />
                      </InputGroup>

                      <InputGroup label="Photo">
                        {sp.photo && (
                          <div className="mb-2">
                            <img
                              src={sp.photo}
                              alt="speaker"
                              className="h-16 w-16 rounded-lg object-cover border"
                            />
                            <p className="text-xs text-gray-500 break-all mt-1">{sp.photo}</p>
                          </div>
                        )}
                          <input
                            type="file"
                            accept="image/*"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              setSpeakerPickedName((p) => ({ ...p, [idx]: file.name }));
                              setSpeakerUploading((p) => ({ ...p, [idx]: true }));
                              try {
                                await uploadSpeakerPhoto(idx, file);
                              } finally {
                                setSpeakerUploading((p) => ({ ...p, [idx]: false }));
                                // ✅ optional: clear AFTER upload finishes
                                e.target.value = "";
                              }
                            }}
                          />
                          {speakerPickedName[idx] && (
                            <p className="text-xs text-gray-500 mt-2">
                              Selected: <span className="font-mono">{speakerPickedName[idx]}</span>
                              {speakerUploading[idx] ? " (uploading...)" : ""}
                            </p>
                          )}

                      </InputGroup>
                    </div>
                  </div>
                ))}

                <button
                  type="button"
                  onClick={addSpeaker}
                  className="px-4 py-2 rounded-xl border border-gray-200 hover:bg-gray-50"
                >
                  + Add Speaker
                </button>
              </div>
            </Card>

            <Card title="What will you get?">
              <div className="space-y-3">
                {whatYouGet.map((item, idx) => (
                  <div key={idx} className="flex gap-2">
                    <input
                      className="flex-1 border-2 border-gray-200 rounded-xl px-4 py-3"
                      value={item}
                      onChange={(e) => updateWhatYouGet(idx, e.target.value)}
                      placeholder="e.g. Certificate, Free snacks, Recording access..."
                    />
                    <button
                      type="button"
                      onClick={() => removeWhatYouGet(idx)}
                      disabled={whatYouGet.length <= 1}
                      className="px-3 py-2 border-2 border-gray-200 rounded-xl hover:bg-gray-50 disabled:opacity-50"
                    >
                      Remove
                    </button>
                  </div>
                ))}

                <button
                  type="button"
                  onClick={addWhatYouGet}
                  className="px-4 py-2 border-2 border-gray-200 rounded-xl hover:bg-gray-50"
                >
                  + Add item
                </button>
              </div>
            </Card>
          </div>
          <div className="xl:col-span-7 space-y-5">
            <Card title="Event Content (Markdown)">
              <div className="flex items-center gap-3 mb-4">
                <label className="px-3 py-2 border rounded-lg cursor-pointer text-sm">
                  Insert Image
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleMarkdownTempImage(file);
                      e.target.value = "";
                    }}
                  />
                </label>
                <span className="text-xs text-gray-500">
                  Image upload.
                </span>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <textarea
                  ref={textareaRef}
                  className="w-full min-h-[520px] border-2 border-gray-200 rounded-xl p-4 font-mono text-sm"
                  value={markdown}
                  onChange={(e) => setMarkdown(e.target.value)}
                  placeholder="Write event content..."
                />

                <div className="w-full min-h-[520px] border-2 border-gray-200 rounded-xl p-4 bg-gray-50 overflow-auto">
                <ReactMarkdown
                  rehypePlugins={[
                    rehypeRaw,
                    [
                      rehypeSanitize,
                      {
                        ...defaultSchema,
                        attributes: {
                          ...defaultSchema.attributes,
                          img: ["src", "alt", "title"],
                        },
                      },
                    ],
                  ]}
                >
                  {markdown}
                </ReactMarkdown>

                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
